import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAppSelector } from '../store/hooks';
import { selectSelectedMuseum } from '../store/slices/museumSlice';

interface Museum {
  id: number;
  name: string;
  museum_code: string;
}

const ConciergeWayfindingPage: React.FC = () => {
  const selectedMuseum = useAppSelector(selectSelectedMuseum);
  const [museums, setMuseums] = useState<Museum[]>([]);
  const [activeMuseumCode, setActiveMuseumCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const raasBaseUrl = process.env.REACT_APP_RAAS_BASE_URL || '';

  const fetchMuseums = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/museums/', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const museumsData: Museum[] = data.results || data;
        setMuseums(museumsData);
        if (museumsData.length > 0 && !activeMuseumCode) {
          setActiveMuseumCode(museumsData[0].museum_code);
        }
      }
    } catch (err) {
      console.error('Error fetching museums:', err);
    } finally {
      setLoading(false);
    }
  }, [activeMuseumCode]);

  useEffect(() => {
    if (!selectedMuseum) {
      fetchMuseums();
    }
  }, [selectedMuseum, fetchMuseums]);

  const getIframeUrl = (museumCode: string) => `${raasBaseUrl}/api-ws/routing/?isuser=true&museum=${museumCode}`;

  const currentMuseumCode = selectedMuseum ? selectedMuseum.museum_code : activeMuseumCode;

  if (loading && !currentMuseumCode) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentMuseumCode) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
        <Typography color="text.secondary">No museums available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {!selectedMuseum && museums.length > 1 && (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {museums.map(museum => (
            <Button
              key={museum.id}
              size="small"
              variant={activeMuseumCode === museum.museum_code ? 'contained' : 'outlined'}
              onClick={() => setActiveMuseumCode(museum.museum_code)}
            >
              {museum.name}
            </Button>
          ))}
        </Box>
      )}
      <Box sx={{ flexGrow: 1 }}>
        <iframe
          key={currentMuseumCode}
          src={getIframeUrl(currentMuseumCode)}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          title="Concierge Wayfinding"
        />
      </Box>
    </Box>
  );
};

export default ConciergeWayfindingPage;
